// Persistent device storage (docs/04): device token, screen id, and the last
// manifest — all in IndexedDB so the player can cold-boot from the persisted
// manifest with no network. One object store used as a simple key→value bag;
// IndexedDB (not localStorage) because the manifest can be large and we want it
// to survive alongside the Cache API in the same origin storage bucket.

import type { Manifest } from './types.ts';
import type { PlaybackEvent } from './events.ts';
import type { MediaLedger } from './quota.ts';

const DB_NAME = 'sl-player';
const DB_VERSION = 1;
const STORE = 'kv';

const K_TOKEN = 'device_token';
const K_SCREEN = 'screen_id';
const K_MANIFEST = 'manifest';
const K_EVENTS = 'events';
const K_LEDGER = 'media_ledger';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function get<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function put(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function del(key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export interface DeviceIdentity {
  token: string;
  screenId: string;
}

export async function loadIdentity(): Promise<DeviceIdentity | null> {
  const [token, screenId] = await Promise.all([get<string>(K_TOKEN), get<string>(K_SCREEN)]);
  if (typeof token === 'string' && token && typeof screenId === 'string') {
    return { token, screenId };
  }
  return null;
}

export async function saveIdentity(id: DeviceIdentity): Promise<void> {
  await Promise.all([put(K_TOKEN, id.token), put(K_SCREEN, id.screenId)]);
}

export async function loadManifest(): Promise<Manifest | null> {
  return (await get<Manifest>(K_MANIFEST)) ?? null;
}

export async function saveManifest(m: Manifest): Promise<void> {
  await put(K_MANIFEST, m);
}

/** Persisted playback-event buffer (survives reload/power loss; see events.ts). */
export async function loadEvents(): Promise<PlaybackEvent[]> {
  return (await get<PlaybackEvent[]>(K_EVENTS)) ?? [];
}

export async function saveEvents(events: PlaybackEvent[]): Promise<void> {
  await put(K_EVENTS, events);
}

/**
 * Per-URL byte ledger backing the quota pre-flight (quota.ts). The manifest
 * carries no media sizes, so we remember what each download actually cost and use
 * it to predict the next swap's footprint.
 */
export async function loadMediaLedger(): Promise<MediaLedger> {
  return (await get<MediaLedger>(K_LEDGER)) ?? {};
}

export async function saveMediaLedger(ledger: MediaLedger): Promise<void> {
  await put(K_LEDGER, ledger);
}

/**
 * Wipe all device state (token revoked / factory reset). Clears IndexedDB keys;
 * media/shell caches are cleared separately by the caller so this stays a pure
 * identity wipe.
 */
export async function wipeIdentity(): Promise<void> {
  await Promise.all([del(K_TOKEN), del(K_SCREEN), del(K_MANIFEST), del(K_EVENTS), del(K_LEDGER)]);
}
